import type { CadDocument } from '../model/document';
import {
  collapseWarnings,
  type CadFormat,
  type CadFormatAdapter,
  type ReadResult,
  type WriteOptions,
  type WriteResult,
} from './adapter';
import { readDxf } from './dxf/reader';
import { writeDxf } from './dxf/writer';

/**
 * DXF 어댑터 (작업지시서 P3-01 ~ P3-03)
 *
 * 브라우저와 변환 워커 양쪽에서 같은 코드가 돈다 — 순수 TS 이므로 가능하다.
 * DWG/DWT 는 이 어댑터가 다루지 않는다. 워커의 LibreDwgAdapter 가
 * DWG → DXF 로 바꿔 준 뒤 이 어댑터로 넘어온다.
 */
export class DxfAdapter implements CadFormatAdapter {
  readonly name = 'dxf';

  canRead(fmt: CadFormat): boolean {
    return fmt === 'dxf';
  }

  canWrite(fmt: CadFormat): boolean {
    return fmt === 'dxf';
  }

  async read(buf: ArrayBuffer, fmt: CadFormat): Promise<ReadResult> {
    if (fmt !== 'dxf') throw new Error(`DxfAdapter 가 다루지 않는 형식입니다: ${fmt}`);
    const { doc, warnings } = readDxf(buf);
    return { doc, warnings: collapseWarnings(warnings) };
  }

  async write(doc: CadDocument, fmt: CadFormat, opt?: WriteOptions): Promise<WriteResult> {
    if (fmt !== 'dxf') throw new Error(`DxfAdapter 가 다루지 않는 형식입니다: ${fmt}`);
    const { data, warnings } = writeDxf(doc, opt);
    return { data, warnings: collapseWarnings(warnings) };
  }
}

export const dxfAdapter = new DxfAdapter();

/* -------------------------------------------------------------------------- *
 * 편의 함수 — 화면에서 바로 쓰는 경로
 * -------------------------------------------------------------------------- */

export async function parseDxfFile(file: File | Blob): Promise<ReadResult> {
  return dxfAdapter.read(await file.arrayBuffer(), 'dxf');
}

export async function exportDxfBlob(doc: CadDocument, opt?: WriteOptions): Promise<Blob> {
  const { data } = await dxfAdapter.write(doc, 'dxf', opt);
  // BufferSource 로 넘겨야 Blob 이 사본을 만든다
  return new Blob([data as unknown as ArrayBuffer], { type: 'application/dxf' });
}
